import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Button from '../../../uikit/button';
import InfiniteScrollDropdown from '../../../uikit/infinite_scroll_dropdown';
import Input from '../../../uikit/input';
import Textarea from '../../../uikit/textarea';
import DropdownMenu from '../../../uikit/dropdown_menu';
import { completePO } from '../../../utils/apiUtils/inventoryUtils';
import { API_INVENTORY_BASE_URL } from '../../../apiConstant';
import smallBg7 from '../../../utils/images/inapp/smallbg7.jpg';
import usePODropdowns from '../PODropdownUtil';
import { FILE_TYPES } from '../../../utils/constants';
import { useS3FilesByType } from '../../../utils/hooks/useS3FilesByType';
import DragDrop2 from '../../../uikit/drag_and_drop/DargDrop2';
import DisplayFiles from '../../../modules/project/components/pages/labour-payments/DisplayFiles';

export default function CompleteWoPoModal({
  show,
  onClose,
  selectedItem,
  onAction,
  workOrder,
  warehouseList,
  returnIndex,
  fromMi = false,
}) {
  const [loading, setLoading] = useState(false);
  const [toSite, setToSite] = useState(null);
  const [quantity, setQuantity] = useState(selectedItem?.quantity || 0);
  const [receivedNotes, setReceivedNotes] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [errors, setErrors] = useState({});

  // Batch selection state
  const [isNewBatch, setIsNewBatch] = useState(false);
  const [batchId, setStockBatchId] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [batchPrice, setBatchPrice] = useState('');

  // Whether batch selection applies at all is driven by the currently
  // selected To Site (either the work order's default site, or one chosen
  // from the To Site dropdown, both of which carry a `batchwise` flag).
  const isBatchwise = !!toSite?.batchwise;

  useEffect(() => {
    if (show) {
      const prefillPrice = selectedItem?.poQuantityPriceDetails?.price;
      setBatchPrice(prefillPrice != null ? String(prefillPrice) : '');
    }
  }, [show, selectedItem]);

  const { fetchWarehouses, handleFetchError, transformWarehouseOption } =
    usePODropdowns();
  const queryClient = useQueryClient();

  const inventoryName =
    selectedItem?.poQuantityPriceDetails?.inventoryDetails?.inventoryName ||
    selectedItem?.inventoryDetails?.inventoryName;
  const workOrderId = selectedItem?.workOrderId ?? workOrder?.workOrderId;
  const quantityId = selectedItem?.poQuantityPriceId;
  const returnKey =
    workOrderId && inventoryName ? `${workOrderId}|${inventoryName}` : null;
  const returnedQty = returnKey ? returnIndex?.[returnKey] || 0 : 0;
  function resolvePoLine(workOrder, selectedItem) {
    if (selectedItem?.poQuantityPriceDetails) {
      return selectedItem.poQuantityPriceDetails;
    }
    return workOrder?.poQuantityPriceList?.find(
      (p) => p.poQuantityPriceId === selectedItem?.poQuantityPriceId
    );
  }

  const poLine = resolvePoLine(workOrder, selectedItem);
  const poTotal = poLine?.quantity ?? 0;
  const poCompleted = poLine?.quantityCompleted ?? 0;
  const maxQuantity = Math.max(poTotal - poCompleted - returnedQty, 0);

  const { data: files } = useS3FilesByType({
    type: FILE_TYPES.PO_PHOTOS,
    id: workOrderId,
  });


  const batchQueryType = 'PO';

  const { data: stockBatches } = useQuery({
    queryKey: ['stock-batches-quantity', quantityId, batchQueryType],
    queryFn: async () => {
      const res = await axios.get(`${API_INVENTORY_BASE_URL}/stock-batches/quantity`, {
        params: { quantityId, type: batchQueryType },
      });
      return res.data || [];
    },
    enabled: show && !!quantityId && isBatchwise,
  });

  const batchOptions = (stockBatches || []).map((b) => ({
    label: b.batchName,
    value: b.batchId,
    description: `Current Stock: ${b.batchQuantity}`,
  }));

  // Clears any in-progress batch selection. Used whenever the effective
  // To Site changes, since a new site may not be batchwise (or may be a
  // different batchwise site with different batch options).
  const resetBatchSelection = () => {
    setIsNewBatch(false);
    setStockBatchId(null);
    setBatchName('');
    setErrors((prev) => ({ ...prev, batch: '' }));
  };

  useEffect(() => {
    const toSiteObj = {
      label: workOrder?.toSite?.roomName,
      value: workOrder?.toSite?.roomId,
      batchwise: workOrder?.toSite?.batchwise,
    };
    if (toSiteObj.value) {
      setToSite(toSiteObj);
    }
  }, [workOrder?.toSite]);

  const handleToSiteSelect = (selected) => {
    setToSite(selected);
    resetBatchSelection();
  };

  const onCompleteClick = async (e) => {
    e.preventDefault();

    if (isBatchwise) {
      if (isNewBatch && !batchName.trim()) {
        setErrors((prev) => ({ ...prev, batch: 'Batch name is required' }));
        return;
      }
      if (!isNewBatch && !batchId) {
        setErrors((prev) => ({ ...prev, batch: 'Please select a batch' }));
        return;
      }
    }

    setLoading(true);
    try {
      const obj = {
        quantityPriceId: selectedItem?.poQuantityPriceId,
        quantityIfChanged: quantity,
        receivedNotes,
        expiryDate,
        ...(isBatchwise
          ? isNewBatch
            ? { batchName: batchName.trim(), price: batchPrice ? Number(batchPrice) : undefined }
            : { batchId: Number(batchId) }
          : { batchId: -1 }),
      };

      if (!fromMi) {
        // don't send while calling from MI
        obj.toSiteIfChanged = toSite?.value;
      }

      await completePO(obj);
      window.triggerAlert({ title: 'Completed successfully', type: 'success' });
      onAction();
    } catch (e) {
      console.error(e);
      window.triggerAlert({ title: 'Failed to complete', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  useEffect(() => {
    if (show && selectedItem?.quantity != null) {
      const initialQty = Number(selectedItem.quantity);

      setQuantity(initialQty > maxQuantity ? maxQuantity : initialQty);
    }
  }, [show, selectedItem, maxQuantity]);
  useEffect(() => {
    if (!show) {
      setQuantity(0);
      setReceivedNotes('');
      setExpiryDate('');
      setIsNewBatch(false);
      setStockBatchId(null);
      setBatchName('');
      setErrors({});
    }
  }, [show]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Custom Header with Full Background */}
        <div
          className="relative h-20 flex items-center justify-center"
          style={{
            backgroundImage: `url(${smallBg7})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <h2 className="text-xl font-bold text-white">
            Purchase Order - Update Item Delivery
          </h2>

          {/* Custom Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 bg-orange-500 hover:bg-orange-600 rounded flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.71967 5.71967C6.01256 5.42678 6.48744 5.42678 6.78033 5.71967L12 10.9393L17.2197 5.71967C17.5126 5.42678 17.9874 5.42678 18.2803 5.71967C18.5732 6.01256 18.5732 6.48744 18.2803 6.78033L13.0607 12L18.2803 17.2197C18.5732 17.5126 18.5732 17.9874 18.2803 18.2803C17.9874 18.5732 17.5126 18.5732 17.2197 18.2803L12 13.0607L6.78033 18.2803C6.48744 18.5732 6.01256 18.5732 5.71967 18.2803C5.42678 17.9874 5.42678 17.5126 5.71967 17.2197L10.9393 12L5.71967 6.78033C5.42678 6.48744 5.42678 6.01256 5.71967 5.71967Z"
                fill="white"
                stroke="white"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
          <div>
            <span className="font-bold">Material Name: </span>
            <span>
              {selectedItem?.poQuantityPriceDetails?.inventoryDetails
                ?.inventoryName ||
                selectedItem?.inventoryDetails?.inventoryName}
            </span>
          </div>

          <div>
            <Input
              label="Quantity"
              type="number"
              placeholder="Enter quantity"
              value={quantity}
              max={maxQuantity}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > maxQuantity) {
                  setQuantity(maxQuantity);
                  setErrors((prev) => ({
                    ...prev,
                    quantity: `Max value should be ${maxQuantity} ${selectedItem?.poQuantityPriceDetails?.inventoryDetails?.measurementUnit || selectedItem?.inventoryDetails?.measurementUnit || ''}`,
                  }));
                  return;
                }
                setQuantity(value);
                setErrors((prev) => ({
                  ...prev,
                  quantity: '',
                }));
              }}
              error={errors?.quantity}
              required
            />
          </div>

          {!fromMi && warehouseList && (
            <div>
              <InfiniteScrollDropdown
                label="To Site"
                placeholder="Select To Site"
                fetchFunction={fetchWarehouses}
                queryKey="inventory-room-dropdown-menu"
                transformOption={transformWarehouseOption}
                onFetchError={() =>
                  handleFetchError('Failed to fetch warehouses')
                }
                value={toSite || ''}
                onSelect={handleToSiteSelect}
                disablePortal={false}
              />
            </div>
          )}

          {/* Batch selection - only applicable when the selected To Site is batchwise */}
          {isBatchwise && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-bold text-orange-900">
                  Batch <span className="text-red-600">*</span>
                </label>
                {/* <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">New</span>
                  <button
                    type="button"
                    onClick={() => {
                      setIsNewBatch((prev) => !prev);
                      setStockBatchId(null);
                      setBatchName('');
                      setErrors((prev) => ({ ...prev, batch: '' }));
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${isNewBatch ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                    title="Toggle new batch"
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isNewBatch ? 'translate-x-4' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div> */}
              </div>

              {isNewBatch ? (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Enter batch name"
                    value={batchName}
                    onChange={(e) => {
                      setBatchName(e.target.value);
                      if (errors.batch) setErrors((prev) => ({ ...prev, batch: '' }));
                    }}
                    error={errors?.batch}
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    value={batchPrice}
                    onChange={(e) => setBatchPrice(e.target.value)}
                  />
                </div>
              ) : (
                <>
                  <DropdownMenu
                      value={batchId}
                    onSelect={(val) => {
                      setStockBatchId(val?.value ?? null);
                      if (errors.batch) setErrors((prev) => ({ ...prev, batch: '' }));
                    }}
                    options={batchOptions}
                    placeholder="Select batch"
                    searchable={true}
                    hideCloseOption
                    triggerButtonClassName={`w-full !bg-white ${errors.batch ? '!border-red-400' : '!border-gray-300'}`}
                  />
                  {errors.batch && (
                    <p className="text-red-500 text-xs mt-1">{errors.batch}</p>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <Input
              label="Expiry Date"
              type="date"
              placeholder="Select expiry date"
              value={expiryDate}
              min={new Date(new Date().getTime() + 86400000).toISOString().split('T')[0]} // Allow tomorrow onwards
              onChange={(e) => setExpiryDate(e.target.value)}
              parentClassName="mb-1"
              inputClassName="text-base"
              labelClassName="!text-orange-900 !font-bold"
            />
          </div>

          <div>
            <Textarea
              label="Notes"
              placeholder="Enter completion notes"
              value={receivedNotes}
              onChange={(e) => setReceivedNotes(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <div className="text-[16px] font-semibold mb-2 mt-4 text-gray-800">
              Upload Delivery Photos
            </div>
            {workOrderId ? (
              <>
                <DragDrop2
                  onError={() => {}}
                  subtitle="Upload photos of the delivered items"
                  type={FILE_TYPES.PO_PHOTOS}
                  format='image/*'
                  id={workOrderId}
                  onUpload={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['s3-files', FILE_TYPES.PO_PHOTOS, workOrderId],
                    });
                  }}
                  onDelete={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['s3-files', FILE_TYPES.PO_PHOTOS, workOrderId],
                    });
                  }}
                  // className={'h-32 mb-4'}
                />
                <div>
                  <DisplayFiles
                    files={files}
                    onDelete={() => {
                      queryClient.invalidateQueries({
                        queryKey: [
                          's3-files',
                          FILE_TYPES.PO_PHOTOS,
                          workOrderId,
                        ],
                      });
                    }}
                    enableDelete={true}
                  />
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 italic">
                Upload will be available once the work order is saved.
              </div>
            )}
          </div>
        </div>

        {/* Custom Footer */}
        <div className="px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={onCompleteClick}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-3 w-full rounded-lg"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
}

CompleteWoPoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
  selectedItem: PropTypes.shape({
    workOrderId: PropTypes.number,
    poQuantityPriceId: PropTypes.number,
    quantity: PropTypes.number,
    inventoryDetails: PropTypes.shape({
      inventoryName: PropTypes.string,
      measurementUnit: PropTypes.string,
    }),
    poQuantityPriceDetails: PropTypes.shape({
      inventoryDetails: PropTypes.shape({
        inventoryName: PropTypes.string,
        measurementUnit: PropTypes.string,
      }),
      price: PropTypes.number
    }),
  }),
  workOrder: PropTypes.shape({
    toSite: PropTypes.shape({
      roomId: PropTypes.number,
      roomName: PropTypes.string,
      batchwise: PropTypes.bool,
    }),
    workOrderId: PropTypes.number,
    poQuantityPriceDetails: PropTypes.shape({
      quantity: PropTypes.number,
      quantityCompleted: PropTypes.number,
    }),
    poQuantityPriceList: PropTypes.arrayOf(
      PropTypes.shape({
        poQuantityPriceId: PropTypes.number,
        quantity: PropTypes.number,
        quantityCompleted: PropTypes.number,
        price: PropTypes.number,
      })
    ),
  }),
  warehouseList: PropTypes.arrayOf(
    PropTypes.shape({
      roomId: PropTypes.number,
      roomName: PropTypes.string,
      batchwise: PropTypes.bool,
    })
  ),
  fromMi: PropTypes.bool,
  returnIndex: PropTypes.objectOf(PropTypes.number),
};
